import { useMemo } from "react";
import type { CategoryType } from "@/components/game-categories";
import {
  CATEGORY_ESCAPE_ROOM,
  CATEGORY_HAUNT,
} from "@/components/game-categories/constants";
import { hasCategory } from "@/components/game-categories/helpers";

import {
  CompletedActiveIcon,
  CompletedIcon,
  EscapesActiveIcon,
  EscapesIcon,
  ExperiencesActiveIcon,
  ExperiencesIcon,
  HauntsActiveIcon,
  HauntsIcon,
} from "./icons";

const GameStatusEnum = {
  CS: "CS", // Coming Soon
  OP: "OP", // Open
  PC: "PC", // Permanently Closed
  TC: "TC", // Temporarily Closed
} as const;

interface GameType {
  isCompleted?: boolean;
  primaryCategory: CategoryType;
  status: keyof typeof GameStatusEnum;
}

interface LocationType {
  company: {
    name: string;
  };
  displayName?: string;
  games: GameType[];
}

interface CategoryPinProps {
  icon?: React.ComponentType<React.ComponentProps<"svg">>;
  isActive?: boolean;
  location: LocationType;
}

export default function CategoryPin({
  icon: Icon,
  isActive = false,
  location,
}: CategoryPinProps) {
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: legacy
  const icon = useMemo(() => {
    if (Icon) {
      return <Icon />;
    }

    if (location.games.every((game) => game.isCompleted)) {
      return isActive ? <CompletedActiveIcon /> : <CompletedIcon />;
    }

    const isEmpty = !location.games.some((game) =>
      [GameStatusEnum.CS, GameStatusEnum.OP].includes(
        game.status as typeof GameStatusEnum.CS | typeof GameStatusEnum.OP
      )
    );
    const state = isEmpty ? "inactive" : "normal";

    const isAllEscapes = location.games.every((game) =>
      game.primaryCategory
        ? hasCategory(game.primaryCategory, CATEGORY_ESCAPE_ROOM)
        : false
    );
    if (isAllEscapes) {
      return isActive ? <EscapesActiveIcon /> : <EscapesIcon state={state} />;
    }

    const isAllHaunts = location.games.every((game) =>
      game.primaryCategory
        ? hasCategory(game.primaryCategory, CATEGORY_HAUNT)
        : false
    );
    if (isAllHaunts) {
      return isActive ? <HauntsActiveIcon /> : <HauntsIcon state={state} />;
    }

    return isActive ? (
      <ExperiencesActiveIcon />
    ) : (
      <ExperiencesIcon state={state} />
    );
  }, [location, isActive, Icon]);

  return (
    <div className="cursor-pointer">
      <div className="flex h-17.5 w-full items-center justify-center">
        {icon}
      </div>
      <p className="line-clamp-1 max-w-25 text-center font-medium text-11 text-heather text-shadow-0-0-6 text-shadow-rich-black/30">
        {location.displayName || location.company.name}
      </p>
    </div>
  );
}
